import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import { useServiceLists, useDeleteServiceList } from "hooks/useServiceList"
import { formatReadableDate } from "utils/formatDate"

import ServiceListAddEdit from "./ServiceListAddEdit"
import ServiceListHeader from "./ServiceListHeader"

const ServiceListPage = () => {
  document.title = "Service Lists"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useServiceLists(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteServiceList()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Service list deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      toast.error("Failed to delete service list.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Parent",
        cell: ({ row }) => row.original.parent?.name || "-",
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }) => row.original.description || "-",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(row.original)}
                id={`edit-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${row.original.id}`}>
                Edit
              </UncontrolledTooltip>
            </li>
            <li>
              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(row.original)}
                id={`delete-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </li>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper
      title="Service Lists"
      breadcrumbItem="Service Lists"
    >
      <ServiceListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <ServiceListAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />
    </PageWrapper>
  )
}

export default ServiceListPage
